import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, CircleAlert, ShieldCheck, ShieldOff, Copy, Eye, EyeOff } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { Card, Spinner } from "@/components/admin/shared";

const TABS = [["tracking", "Tracking"], ["email", "Email Flows"], ["payments", "Payments"], ["shipping", "Shipping"], ["account", "Account"]];

const Input = ({ label, value, onChange, placeholder, type = "text", testid, hint, secure = false }) => {
  const [reveal, setReveal] = useState(false);
  const inputType = secure && !reveal ? "password" : type;
  return (
    <label className="block">
      <span className="text-xs font-medium text-[#6E6168]">{label}</span>
      <div className="relative">
        <input type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
          autoComplete={secure ? "off" : undefined}
          className={`mt-1.5 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm text-[#2C2B2C] focus:border-[#FBC4A8] focus:outline-none ${secure ? "pr-11" : ""}`} />
        {secure && (
          <button type="button" onClick={() => setReveal((r) => !r)} data-testid={testid ? `${testid}-reveal` : undefined}
            aria-label={reveal ? "Hide value" : "Reveal value"}
            className="absolute right-3 top-1/2 -translate-y-1/2 mt-[3px] text-[#B6A99F] transition-colors hover:text-[#6E6168]">
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {hint && <span className="mt-1 block text-[0.68rem] text-[#B6A99F]">{hint}</span>}
    </label>
  );
};

const Area = ({ label, value, onChange, placeholder, testid, hint }) => (
  <label className="block">
    <span className="text-xs font-medium text-[#6E6168]">{label}</span>
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} data-testid={testid}
      className="mt-1.5 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 font-mono text-xs text-[#2C2B2C] focus:border-[#FBC4A8] focus:outline-none" />
    {hint && <span className="mt-1 block text-[0.68rem] text-[#B6A99F]">{hint}</span>}
  </label>
);

const Toggle = ({ label, checked, onChange }) => (
  <button type="button" onClick={() => onChange(!checked)} className="flex items-center justify-between rounded-xl border border-[#E5DBC8] bg-white px-4 py-3 text-left">
    <span className="text-sm text-[#2C2B2C]">{label}</span>
    <span className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-[#FBC4A8]" : "bg-[#E5DBC8]"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-4" : "left-0.5"}`} />
    </span>
  </button>
);

const Status = ({ ok, on, off }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[0.62rem] font-semibold uppercase tracking-wider ${ok ? "bg-[#E3F0E3] text-[#4A5D4E]" : "bg-[#F5E9DD] text-[#B07A4E]"}`}>
    {ok ? <Check className="h-3 w-3" /> : <CircleAlert className="h-3 w-3" />} {ok ? on : off}
  </span>
);

export function SettingsPanel() {
  const [tab, setTab] = useState("tracking");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [tfa, setTfa] = useState({ enabled: false, setup: null, code: "", busy: false, backup: null, disablePw: "", disableCode: "", showDisable: false });

  const loadTfa = async () => {
    try { const { data } = await adminApi.get("/auth/me"); setTfa((p) => ({ ...p, enabled: !!data.totp_enabled })); } catch { /* ignore */ }
  };

  const startTfaSetup = async () => {
    setTfa((p) => ({ ...p, busy: true }));
    try {
      const { data } = await adminApi.post("/auth/2fa/setup");
      setTfa((p) => ({ ...p, setup: data, code: "", backup: null }));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not start setup"); }
    finally { setTfa((p) => ({ ...p, busy: false })); }
  };

  const enableTfa = async () => {
    setTfa((p) => ({ ...p, busy: true }));
    try {
      const { data } = await adminApi.post("/auth/2fa/enable", { code: tfa.code });
      setTfa((p) => ({ ...p, enabled: true, setup: null, code: "", backup: data.backup_codes }));
      toast.success("Two-factor authentication enabled");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Incorrect code"); }
    finally { setTfa((p) => ({ ...p, busy: false })); }
  };

  const disableTfa = async () => {
    setTfa((p) => ({ ...p, busy: true }));
    try {
      await adminApi.post("/auth/2fa/disable", { password: tfa.disablePw, code: tfa.disableCode });
      setTfa((p) => ({ ...p, enabled: false, showDisable: false, disablePw: "", disableCode: "", backup: null }));
      toast.success("Two-factor authentication disabled");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not disable 2FA"); }
    finally { setTfa((p) => ({ ...p, busy: false })); }
  };

  const changePw = async () => {
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwSaving(true);
    try {
      await adminApi.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not update password");
    } finally { setPwSaving(false); }
  };

  useEffect(() => { adminApi.get("/admin/settings").then((r) => setS(r.data)); loadTfa(); }, []);

  const upd = (section, key, val) => setS((p) => ({ ...p, [section]: { ...p[section], [key]: val } }));
  const updFlow = (key, val) => setS((p) => ({ ...p, email: { ...p.email, flows: { ...p.email.flows, [key]: val } } }));

  const save = async (section) => {
    setSaving(true);
    try {
      await adminApi.put("/admin/settings", { [section]: s[section] });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <Spinner />;
  const SaveBtn = ({ section }) => (
    <button onClick={() => save(section)} disabled={saving} data-testid={`save-${section}`}
      className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-[#232222] px-6 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-60">
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
    </button>
  );

  return (
    <div data-testid="settings-panel">
      <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Settings</h1>
      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}
            className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-wider transition-all ${tab === k ? "bg-[#232222] text-[#F5EFE3]" : "border border-[#DACDB4] text-[#8A7F86]"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-6 max-w-2xl">
        {tab === "account" && (
          <Card className="space-y-4" data-testid="account-settings">
            <p className="text-sm text-[#8A7F86]">Change your admin password. Choose something strong and unique — this protects your entire store. You'll stay logged in on this device.</p>
            <Input label="Current password" type="password" testid="pw-current" value={pw.current} onChange={(v) => setPw((p) => ({ ...p, current: v }))} />
            <Input label="New password" type="password" testid="pw-new" value={pw.next} onChange={(v) => setPw((p) => ({ ...p, next: v }))} hint="At least 8 characters." />
            <Input label="Confirm new password" type="password" testid="pw-confirm" value={pw.confirm} onChange={(v) => setPw((p) => ({ ...p, confirm: v }))} />
            <button onClick={changePw} disabled={pwSaving} data-testid="pw-submit"
              className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-[#232222] px-6 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-60">
              {pwSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Update password
            </button>

            {/* Two-factor authentication */}
            <div className="mt-6 border-t border-[#F2E8E6] pt-6" data-testid="tfa-section">
              <div className="flex items-center justify-between">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#2C2B2C]"><ShieldCheck className="h-4 w-4 text-[#FBC4A8]" /> Two-factor authentication</p>
                  <p className="mt-1 text-xs text-[#8A7F86]">Add a second step at login using Google Authenticator, Authy or 1Password.</p>
                </div>
                <Status ok={tfa.enabled} on="On" off="Off" />
              </div>

              {tfa.backup && (
                <div className="mt-4 rounded-xl border border-[#CDE9DA] bg-[#EDF7F1] p-4" data-testid="tfa-backup-codes">
                  <p className="text-xs font-semibold text-[#2C2B2C]">Save your backup codes</p>
                  <p className="mt-1 text-[0.7rem] text-[#6E6168]">Each code works once if you lose your phone. Store them somewhere safe — you won't see them again.</p>
                  <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-xs text-[#2C2B2C]">
                    {tfa.backup.map((c) => <span key={c} className="rounded bg-white px-2 py-1 text-center">{c}</span>)}
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(tfa.backup.join("\n")); toast.success("Backup codes copied"); }}
                    data-testid="tfa-copy-backup" className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#3B8C6E]">
                    <Copy className="h-3.5 w-3.5" /> Copy codes
                  </button>
                </div>
              )}

              {!tfa.enabled && !tfa.setup && (
                <button onClick={startTfaSetup} disabled={tfa.busy} data-testid="tfa-setup-btn"
                  className="mt-4 flex items-center gap-1.5 rounded-full border border-[#232222] px-6 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#232222] disabled:opacity-60">
                  {tfa.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Enable 2FA
                </button>
              )}

              {!tfa.enabled && tfa.setup && (
                <div className="mt-4 space-y-3" data-testid="tfa-setup">
                  <p className="text-xs text-[#6E6168]">1. Scan this QR code in your authenticator app.</p>
                  <img src={tfa.setup.qr} alt="2FA QR code" data-testid="tfa-qr" className="h-40 w-40 rounded-xl border border-[#F2E8E6]" />
                  <p className="text-[0.7rem] text-[#8A7F86]">Can't scan? Enter this key manually: <span className="select-all font-mono text-[#2C2B2C]">{tfa.setup.secret}</span></p>
                  <p className="text-xs text-[#6E6168]">2. Enter the 6-digit code it shows to confirm.</p>
                  <div className="flex gap-2">
                    <input data-testid="tfa-code-input" inputMode="numeric" value={tfa.code} onChange={(e) => setTfa((p) => ({ ...p, code: e.target.value }))} placeholder="123456"
                      className="w-40 rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-center tracking-[0.3em] text-[#2C2B2C] focus:border-[#FBC4A8] focus:outline-none" />
                    <button onClick={enableTfa} disabled={tfa.busy || tfa.code.length < 6} data-testid="tfa-confirm-btn"
                      className="rounded-full bg-[#232222] px-5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-60">
                      {tfa.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm"}
                    </button>
                  </div>
                </div>
              )}

              {tfa.enabled && !tfa.showDisable && (
                <button onClick={() => setTfa((p) => ({ ...p, showDisable: true }))} data-testid="tfa-disable-btn"
                  className="mt-4 flex items-center gap-1.5 rounded-full border border-[#C0392B]/40 px-6 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#C0392B]">
                  <ShieldOff className="h-3.5 w-3.5" /> Disable 2FA
                </button>
              )}

              {tfa.enabled && tfa.showDisable && (
                <div className="mt-4 space-y-3" data-testid="tfa-disable">
                  <p className="text-xs text-[#6E6168]">Confirm your password and a current code to turn off 2FA.</p>
                  <Input label="Password" type="password" testid="tfa-disable-pw" value={tfa.disablePw} onChange={(v) => setTfa((p) => ({ ...p, disablePw: v }))} />
                  <Input label="Authenticator code" testid="tfa-disable-code" value={tfa.disableCode} onChange={(v) => setTfa((p) => ({ ...p, disableCode: v }))} />
                  <div className="flex gap-2">
                    <button onClick={disableTfa} disabled={tfa.busy} data-testid="tfa-disable-confirm"
                      className="rounded-full bg-[#C0392B] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-white disabled:opacity-60">
                      {tfa.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Turn off 2FA"}
                    </button>
                    <button onClick={() => setTfa((p) => ({ ...p, showDisable: false, disablePw: "", disableCode: "" }))}
                      className="rounded-full border border-[#DACDB4] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#8A7F86]">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
        {tab === "tracking" && (
          <Card className="space-y-4">
            <p className="text-sm text-[#8A7F86]">Paste your IDs or full snippets. They load on the storefront after cookie consent. No code changes needed.</p>
            <Input label="Google Analytics 4 (Measurement ID)" testid="set-ga4" value={s.tracking.ga4_id} onChange={(v) => upd("tracking", "ga4_id", v)} placeholder="G-XXXXXXX" />
            <Input label="Google Tag Manager ID" testid="set-gtm" value={s.tracking.gtm_id} onChange={(v) => upd("tracking", "gtm_id", v)} placeholder="GTM-XXXXXX" />
            <Input label="Meta (Facebook) Pixel ID" testid="set-meta" value={s.tracking.meta_pixel_id} onChange={(v) => upd("tracking", "meta_pixel_id", v)} placeholder="1234567890" />
            <Input label="TikTok Pixel ID" testid="set-tiktok" value={s.tracking.tiktok_id} onChange={(v) => upd("tracking", "tiktok_id", v)} placeholder="CXXXXXXXXXXXX" />
            <Input label="Google Ads Conversion ID" testid="set-gads-id" value={s.tracking.google_ads_id} onChange={(v) => upd("tracking", "google_ads_id", v)} placeholder="AW-XXXXXXXXXX" />
            <Input label="Google Ads Purchase Conversion Label" testid="set-gads-label" value={s.tracking.google_ads_purchase_label} onChange={(v) => upd("tracking", "google_ads_purchase_label", v)} placeholder="AbC-D_efGhIjKlmn" />
            <Area label="Custom <head> code" testid="set-head" value={s.tracking.custom_head} onChange={(v) => upd("tracking", "custom_head", v)} placeholder="<!-- any verification or script tags -->" />
            <Area label="Custom <body> code" testid="set-body" value={s.tracking.custom_body} onChange={(v) => upd("tracking", "custom_body", v)} placeholder="<!-- noscript pixels etc -->" />
            <SaveBtn section="tracking" />
          </Card>
        )}

        {tab === "email" && (
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#8A7F86]">Transactional emails via Resend.</p>
              <Status ok={!!s.email.resend_api_key} on="Connected" off="Add API key" />
            </div>
            <Input label="Resend API Key" testid="set-resend-key" secure value={s.email.resend_api_key} onChange={(v) => upd("email", "resend_api_key", v)} placeholder="re_xxxxxxxx" hint="Get one at resend.com → API Keys (after verifying your sending domain)." />
            <Input label="Sender email" testid="set-sender-email" value={s.email.sender_email} onChange={(v) => upd("email", "sender_email", v)} placeholder="orders@auraglowwellness.com" />
            <Input label="Sender name" testid="set-sender-name" value={s.email.sender_name} onChange={(v) => upd("email", "sender_name", v)} placeholder="Aura Glow Wellness" />
            <p className="pt-2 text-xs font-medium text-[#6E6168]">Flows</p>
            <Toggle label="Order confirmation" checked={s.email.flows?.order_confirmation} onChange={(v) => updFlow("order_confirmation", v)} />
            <Toggle label="Shipping notification" checked={s.email.flows?.shipping} onChange={(v) => updFlow("shipping", v)} />
            <Toggle label="Abandoned cart (coming soon)" checked={s.email.flows?.abandoned_cart} onChange={(v) => updFlow("abandoned_cart", v)} />
            <SaveBtn section="email" />
          </Card>
        )}

        {tab === "payments" && (
          <Card className="space-y-5">
            <p className="text-sm text-[#8A7F86]">Connect the ways customers pay. Secret keys are stored securely server-side and never exposed to the storefront.</p>

            {/* Stripe */}
            <div className="rounded-xl border border-[#E5DBC8] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2C2B2C]">Stripe — Cards</span>
                <Status ok={!!s.payments.stripe_publishable_key} on="Connected · Live" off="Add publishable key" />
              </div>
              <p className="mt-2 mb-3 text-xs text-[#8A7F86]">Add your Stripe keys (Stripe → Developers → API keys). The <b>secret key</b> (<code>sk_live_</code>) stays server-side; the <b>publishable key</b> (<code>pk_live_</code>) shows card fields on checkout.</p>
              <Input label="Stripe Secret key" testid="set-stripe-sk" secure value={s.payments.stripe_secret_key} onChange={(v) => upd("payments", "stripe_secret_key", v)} placeholder="sk_live_..." />
              <div className="mt-3"><Input label="Stripe Publishable key" testid="set-stripe-pk" value={s.payments.stripe_publishable_key} onChange={(v) => upd("payments", "stripe_publishable_key", v)} placeholder="pk_live_..." /></div>
              <div className="mt-3"><SaveBtn section="payments" /></div>
            </div>

            {/* Afterpay */}
            <div className="rounded-xl border border-[#E5DBC8] p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2C2B2C]">Afterpay (via Stripe)</span>
                <Status ok={!!s.payments.afterpay_enabled} on="Enabled" off="Turn on in Stripe" />
              </div>
              <p className="mt-2 text-xs text-[#8A7F86]">No separate account needed. Enable Afterpay/Clearpay in Stripe Dashboard → Settings → Payment methods, then flip this on so we show the "4 interest-free payments" messaging.</p>
              <div className="mt-3"><Toggle label="Afterpay is enabled in my Stripe account" checked={s.payments.afterpay_enabled} onChange={(v) => upd("payments", "afterpay_enabled", v)} /></div>
            </div>

            {/* PayPal */}
            <div className="rounded-xl border border-[#E5DBC8] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#2C2B2C]">PayPal</span>
                <Status ok={!!s.payments.paypal_client_id} on="Connected" off="Add keys" />
              </div>
              <p className="text-xs text-[#8A7F86]">From developer.paypal.com → Apps &amp; Credentials. Paste keys, set mode, Save — "Pay with PayPal" appears at checkout instantly.</p>
              <Input label="PayPal Client ID" testid="set-paypal-id" secure value={s.payments.paypal_client_id} onChange={(v) => upd("payments", "paypal_client_id", v)} placeholder="AY..." />
              <Input label="PayPal Secret" testid="set-paypal-secret" secure value={s.payments.paypal_secret} onChange={(v) => upd("payments", "paypal_secret", v)} placeholder="EH..." />
              <label className="block">
                <span className="text-xs font-medium text-[#6E6168]">PayPal mode</span>
                <select value={s.payments.paypal_mode} onChange={(e) => upd("payments", "paypal_mode", e.target.value)} data-testid="set-paypal-mode"
                  className="mt-1.5 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none">
                  <option value="sandbox">Sandbox (testing)</option><option value="live">Live</option>
                </select>
              </label>
            </div>
            <SaveBtn section="payments" />
          </Card>
        )}

        {tab === "shipping" && (
          <Card className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#8A7F86]">Australia Post (MyPost Business) & label sender address.</p>
              <Status ok={!!s.shipping.auspost_api_key} on="Connected" off="Add API key" />
            </div>
            <Input label="AusPost API Key" testid="set-auspost-key" secure value={s.shipping.auspost_api_key} onChange={(v) => upd("shipping", "auspost_api_key", v)} placeholder="MyPost Business API key" />
            <Input label="AusPost Account Number" testid="set-auspost-account" value={s.shipping.auspost_account} onChange={(v) => upd("shipping", "auspost_account", v)} />
            <p className="pt-2 text-xs font-medium text-[#6E6168]">Label sender (shown on shipping labels)</p>
            <Input label="Sender name" testid="set-ship-name" value={s.shipping.sender_name} onChange={(v) => upd("shipping", "sender_name", v)} />
            <Input label="Street address" testid="set-ship-line1" value={s.shipping.sender_line1} onChange={(v) => upd("shipping", "sender_line1", v)} placeholder="5/5 Clark Street" />
            <div className="grid grid-cols-3 gap-3">
              <Input label="Suburb" testid="set-ship-suburb" value={s.shipping.sender_suburb} onChange={(v) => upd("shipping", "sender_suburb", v)} />
              <Input label="State" testid="set-ship-state" value={s.shipping.sender_state} onChange={(v) => upd("shipping", "sender_state", v)} />
              <Input label="Postcode" testid="set-ship-postcode" value={s.shipping.sender_postcode} onChange={(v) => upd("shipping", "sender_postcode", v)} />
            </div>
            <Toggle label="Hide street line on printed labels (show suburb + postcode only)" checked={s.shipping.hide_street} onChange={(v) => upd("shipping", "hide_street", v)} />
            <SaveBtn section="shipping" />
          </Card>
        )}
      </div>
    </div>
  );
}
